import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
    MagnifyingGlass, ArrowsClockwise, CaretRight, Briefcase, Buildings,
    CheckCircle, Spinner, Coins, ShieldCheck, Warning,
} from "@phosphor-icons/react";
import { api, formatApiError } from "../../lib/api";
import { formatINR, formatDate } from "../../lib/format";
import PageHeader from "../../components/layout/PageHeader";
import EmptyState from "../../components/EmptyState";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { cn as classJoin } from "../../lib/utils";
import {
    TECHNICAL_STATES, COMMERCIAL_STATES, SUPPORT_STATES,
    STATE_TONE, STATE_LABEL,
} from "./constants";

export default function ProjectsList() {
    const nav = useNavigate();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({
        q: "",
        technical_state: "",
        commercial_state: "",
        support_state: "",
        has_outstanding: "",
    });

    const load = async () => {
        setLoading(true);
        try {
            const params = Object.fromEntries(
                Object.entries(filters).filter(([, v]) => v !== "" && v !== null && v !== undefined)
            );
            if (params.has_outstanding === "yes") params.has_outstanding = true;
            else if (params.has_outstanding === "no") params.has_outstanding = false;
            else delete params.has_outstanding;
            params.limit = 200;
            const { data: d } = await api.get("/billing/project/list", { params });
            setData(d);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to load projects"));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

    // Aggregate counts for KPI strip — computed locally from the result page.
    const counts = (data?.items || []).reduce(
        (acc, p) => {
            acc.tech[p.technical_state] = (acc.tech[p.technical_state] || 0) + 1;
            acc.comm[p.commercial_state] = (acc.comm[p.commercial_state] || 0) + 1;
            acc.sup[p.support_state] = (acc.sup[p.support_state] || 0) + 1;
            acc.outstanding += Number(p.commercial_summary?.outstanding_total || 0);
            acc.invoiced += Number(p.commercial_summary?.invoiced_total || 0);
            return acc;
        },
        { tech: {}, comm: {}, sup: {}, outstanding: 0, invoiced: 0 },
    );

    if (loading && !data) {
        return (
            <div data-testid="projects-page-loading">
                <PageHeader eyebrow="Engagements" title="Loading projects…" />
            </div>
        );
    }
    if (!data) return null;

    return (
        <div data-testid="projects-list-page">
            <PageHeader
                eyebrow="Engagements · Phase 1"
                title={
                    <span className="flex items-center gap-3">
                        <Briefcase size={22} weight="duotone" className="text-indigo-600" />
                        Projects
                    </span>
                }
                description="Universal operational umbrella over POs, invoices, payments and AMCs. Read-only Phase 1 view — state transitions land in Phase 2."
                actions={
                    <div className="flex items-center gap-2">
                        <Button
                            variant="outline" size="sm"
                            onClick={load}
                            className="gap-1.5"
                            data-testid="projects-refresh-btn"
                        >
                            <ArrowsClockwise size={13} /> Refresh
                        </Button>
                    </div>
                }
            />

            <div className="px-6 sm:px-8 py-8 space-y-6">
                {/* KPI summary strip — 4 tiles */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="projects-kpi-strip">
                    <KpiTile
                        icon={<Briefcase size={16} weight="duotone" />}
                        label="Projects on page"
                        value={data.items.length}
                        sub={`of ${data.total} total`}
                        tone="indigo"
                        testid="kpi-projects-count"
                    />
                    <KpiTile
                        icon={<CheckCircle size={16} weight="duotone" />}
                        label="Completed (delivery)"
                        value={counts.tech.completed || 0}
                        sub={`${counts.tech.in_progress || 0} in-progress`}
                        tone="emerald"
                        testid="kpi-completed"
                    />
                    <KpiTile
                        icon={<Coins size={16} weight="duotone" />}
                        label="Invoiced (on page)"
                        value={formatINR(counts.invoiced, { compact: true })}
                        sub={`${formatINR(counts.outstanding, { compact: true })} outstanding`}
                        tone="amber"
                        testid="kpi-invoiced"
                    />
                    <KpiTile
                        icon={<ShieldCheck size={16} weight="duotone" />}
                        label="Under AMC"
                        value={counts.sup.amc_active || 0}
                        sub={`${counts.sup.inactive || 0} inactive`}
                        tone="violet"
                        testid="kpi-amc-active"
                    />
                </div>

                {/* Filter bar */}
                <div className="reedos-card p-4" data-testid="projects-filters">
                    <div className="grid grid-cols-1 sm:grid-cols-6 gap-3">
                        <div className="sm:col-span-2">
                            <Label className="text-[10px] font-semibold text-slate-500 uppercase">Search</Label>
                            <div className="relative mt-1">
                                <MagnifyingGlass size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                                <Input
                                    value={filters.q}
                                    onChange={(e) => setFilters({ ...filters, q: e.target.value })}
                                    placeholder="Project / proposal / PO number…"
                                    className="pl-8 h-9 text-sm"
                                    data-testid="projects-filter-search"
                                />
                            </div>
                        </div>
                        <FilterSelect
                            label="Technical"
                            value={filters.technical_state}
                            onChange={(v) => setFilters({ ...filters, technical_state: v })}
                            options={TECHNICAL_STATES}
                            testid="projects-filter-technical"
                        />
                        <FilterSelect
                            label="Commercial"
                            value={filters.commercial_state}
                            onChange={(v) => setFilters({ ...filters, commercial_state: v })}
                            options={COMMERCIAL_STATES}
                            testid="projects-filter-commercial"
                        />
                        <FilterSelect
                            label="Support"
                            value={filters.support_state}
                            onChange={(v) => setFilters({ ...filters, support_state: v })}
                            options={SUPPORT_STATES}
                            testid="projects-filter-support"
                        />
                        <div className="flex items-end gap-2">
                            <div className="flex-1">
                                <Label className="text-[10px] font-semibold text-slate-500 uppercase">Outstanding</Label>
                                <select
                                    value={filters.has_outstanding}
                                    onChange={(e) => setFilters({ ...filters, has_outstanding: e.target.value })}
                                    className="mt-1 w-full h-9 px-2 text-xs border border-slate-200 rounded bg-white"
                                    data-testid="projects-filter-outstanding"
                                >
                                    <option value="">Any</option>
                                    <option value="yes">Has outstanding</option>
                                    <option value="no">No outstanding</option>
                                </select>
                            </div>
                            <Button
                                variant="ghost" size="sm"
                                onClick={() => setFilters({
                                    q: "", technical_state: "", commercial_state: "",
                                    support_state: "", has_outstanding: "",
                                })}
                                className="text-[11px] shrink-0"
                                data-testid="projects-filter-reset"
                            >
                                Reset
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Projects table */}
                <div className="reedos-card overflow-hidden" data-testid="projects-table">
                    {data.items.length === 0 ? (
                        <EmptyState
                            icon={<Briefcase size={36} weight="thin" />}
                            title="No projects match the current filters"
                            description="Try clearing the filters or searching by project / PO / proposal number."
                            data-testid="projects-empty"
                        />
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead className="bg-slate-50 border-b border-slate-200">
                                    <tr className="text-left">
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Project</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Client</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Technical</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Commercial</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Support</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Invoiced</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px] text-right">Outstanding</th>
                                        <th className="px-4 py-2 reedos-eyebrow text-[9px]">Created</th>
                                        <th className="px-4 py-2"></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.items.map((p) => {
                                        const cs = p.commercial_summary || {};
                                        return (
                                            <tr
                                                key={p.id}
                                                className="border-t border-slate-100 reedos-row-hover cursor-pointer"
                                                onClick={() => nav(`/projects/${p.id}`)}
                                                data-testid={`projects-row-${p.id}`}
                                            >
                                                <td className="px-4 py-2.5 text-xs">
                                                    <div className="font-mono font-semibold text-slate-900">
                                                        {p.project_number}
                                                    </div>
                                                    {p.source_proposal_number && (
                                                        <div className="text-[10px] text-slate-500 mt-0.5">
                                                            from {p.source_proposal_number}
                                                        </div>
                                                    )}
                                                </td>
                                                <td className="px-4 py-2.5 text-xs">
                                                    <div className="flex items-center gap-1.5">
                                                        <Buildings size={11} className="text-slate-400 shrink-0" />
                                                        <span className="text-slate-800 truncate" title={p.client_name}>
                                                            {p.client_name || "—"}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2.5 text-xs">
                                                    <StateBadge state={p.technical_state} />
                                                </td>
                                                <td className="px-4 py-2.5 text-xs">
                                                    <StateBadge state={p.commercial_state} />
                                                </td>
                                                <td className="px-4 py-2.5 text-xs">
                                                    <StateBadge state={p.support_state} />
                                                </td>
                                                <td className="px-4 py-2.5 text-xs text-right font-mono text-slate-700">
                                                    {formatINR(cs.invoiced_total)}
                                                </td>
                                                <td className="px-4 py-2.5 text-xs text-right font-mono">
                                                    <span className={classJoin(
                                                        "font-semibold",
                                                        Number(cs.outstanding_total) > 0 ? "text-rose-700" : "text-slate-500",
                                                    )}>
                                                        {formatINR(cs.outstanding_total)}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2.5 text-xs text-slate-500">
                                                    {formatDate(p.created_at)}
                                                </td>
                                                <td className="px-4 py-2.5">
                                                    <CaretRight size={14} className="text-slate-300" />
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>

                {/* Footer note explaining Phase 1 boundaries */}
                <div className="reedos-card p-4 border-l-4 border-l-indigo-300 bg-indigo-50/50" data-testid="projects-phase1-note">
                    <div className="flex items-start gap-3">
                        <Warning size={16} className="text-indigo-600 mt-0.5 shrink-0" />
                        <div className="text-xs text-slate-700">
                            <div className="font-semibold mb-1">Phase 1 — Visibility</div>
                            <p className="text-slate-600">
                                This dashboard is <strong>read-only</strong>. Triple-state transitions
                                (Mark Completed, Activate AMC, Close Commercials) ship in Phase 2.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

function KpiTile({ icon, label, value, sub, tone = "indigo", testid }) {
    const toneClasses = {
        indigo: "bg-indigo-50/60 border-indigo-200 text-indigo-700",
        emerald: "bg-emerald-50/60 border-emerald-200 text-emerald-700",
        amber: "bg-amber-50/60 border-amber-200 text-amber-700",
        violet: "bg-violet-50/60 border-violet-200 text-violet-700",
        rose: "bg-rose-50/60 border-rose-200 text-rose-700",
        slate: "bg-slate-50 border-slate-200 text-slate-700",
    }[tone];
    return (
        <div className={classJoin("reedos-card p-3 border-2", toneClasses)} data-testid={testid}>
            <div className="flex items-center gap-1.5 reedos-eyebrow text-[9px]">
                {icon} {label}
            </div>
            <div className="font-display font-bold reedos-data text-2xl mt-1 leading-none text-slate-900">
                {value}
            </div>
            {sub && <div className="text-[10px] text-slate-500 mt-0.5">{sub}</div>}
        </div>
    );
}

function StateBadge({ state }) {
    if (!state) return <span className="text-slate-400 text-xs">—</span>;
    return (
        <span
            className={classJoin(
                "inline-flex items-center rounded px-2 py-0.5 text-[10px] font-semibold border",
                STATE_TONE[state] || "bg-slate-50 text-slate-600 border-slate-200",
            )}
            data-testid={`state-badge-${state}`}
        >
            {STATE_LABEL[state] || state}
        </span>
    );
}

function FilterSelect({ label, value, onChange, options, testid }) {
    return (
        <div>
            <Label className="text-[10px] font-semibold text-slate-500 uppercase">{label}</Label>
            <select
                value={value}
                onChange={(e) => onChange(e.target.value)}
                className="mt-1 w-full h-9 px-2 text-xs border border-slate-200 rounded bg-white"
                data-testid={testid}
            >
                <option value="">All</option>
                {options.map((o) => <option key={o.key} value={o.key}>{o.label}</option>)}
            </select>
        </div>
    );
}
